/* eslint-disable prettier/prettier */
import moment from "moment"
import { DatePickerRange, SelectDropdown } from "quorumdesign"
import React, { useCallback, useState } from "react"
import styled from "styled-components"

interface DateRangeType {
    is_future: boolean
    key: string
    label: string
    order: number
    show_in_enumeration: boolean
    value: number
}
interface DateRangePickerProps {
    publishChange?: (value: string) => void
    deserializedValue?: string
}
interface GlobalDate {
    start?: Date
    stop?: Date
    full?: string
    dateRangeTypeValue?: number
}

const Container = styled.div`
    position: relative;
`

/**
 * This component:
 *  - renders a Date Range Picker dropdown with range options possibility for a custom date range.
 *
 * @param {string} label - the default dropdown label
 * @param {Function} publishChange - callback function. Returns the updated tokenized and serialized GlobalDate object. Required for Filters. Check the method serializedToken() to learn the tokenization schema.
 * @param {string} deserializedValue - gets the value sent back by the filter. It follows the publishChange format.
 */

export const DateRangePicker = (props: DateRangePickerProps) => {
    const today: Date = new Date()
    const customRangeTypeValue = 100
    const label = "Choose a date range... "
    const [showDatePickerRange, setShowDatePickerRange] = useState<boolean>(false)

    const longDateRangeLabelFromStartStopRange = (start: Date, stop: Date): string =>
        start && stop ? moment(start).format("MMM DD, YYYY") + " - " + moment(stop).format("MMM DD, YYYY") : null

    const globalDateFromSerializedToken = (serializedToken: string): GlobalDate => {
        // Parse the serialized parameters in the form of YYYYMMDDYYYYMMDDXX into a GlobalDate object
        if (!serializedToken) return null
        const start = moment(serializedToken.substring(0, 8), "YYYYMMDD").startOf("day").toDate()
        const stop = moment(serializedToken.substring(8, 16), "YYYYMMDD").endOf("day").toDate()
        const full = longDateRangeLabelFromStartStopRange(start, stop)
        const dateRangeTypeValue = parseInt(serializedToken.substring(16)) || customRangeTypeValue
        return { start, stop, dateRangeTypeValue, full }
    }

    const [globalDate, setGlobalDate] = useState<GlobalDate>(
        props.deserializedValue ? globalDateFromSerializedToken(props.deserializedValue) : null,
    )

    const serializedTokenFromGlobalDate = (globalDate: GlobalDate): string =>
        globalDate
            ? moment(globalDate.start).format("YYYYMMDD") +
              moment(globalDate.stop).format("YYYYMMDD") +
              (globalDate?.dateRangeTypeValue || "00")
            : null

    const globalDateFromDateRangetype = (dateRangeType: DateRangeType): GlobalDate => {
        // Figures out the correct date range to filter over
        // Logic from app/querymethods.py date_range
        const today = moment()
        const dateRangeTypeValue = dateRangeType.value
        let start = null
        let stop = null

        switch (dateRangeTypeValue) {
            case DjangIO.app.events.models.DateRangeType.today.value:
                start = today.clone().startOf("day").toDate()
                stop = today.clone().endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.yesterday.value:
                start = today.clone().subtract(1, "day").startOf("day").toDate()
                stop = today.clone().subtract(1, "day").endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.this_week.value:
                start = today.clone().subtract(6, "day").startOf("day").toDate()
                stop = today.clone().endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.this_week_exclusive.value:
                start = today.clone().subtract(7, "day").startOf("day").toDate()
                stop = today.clone().subtract(1, "day").endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.this_month.value:
                start = today.clone().subtract(30, "day").startOf("day").toDate()
                stop = today.clone().subtract(1, "day").endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.this_quarter.value:
                start = today.clone().subtract(90, "day").startOf("day").toDate()
                stop = today.clone().subtract(1, "day").endOf("day").toDate()
                break
            case DjangIO.app.events.models.DateRangeType.this_year.value:
                start = today.clone().subtract(365, "day").startOf("day").toDate()
                stop = today.clone().subtract(1, "day").endOf("day").toDate()
                break
        }
        return {
            start,
            stop,
            full: longDateRangeLabelFromStartStopRange(start, stop),
            dateRangeTypeValue: dateRangeType.value,
        }
    }

    const publish = (globalDate: GlobalDate): void => {
        setGlobalDate(globalDate)
        props.publishChange && props.publishChange(serializedTokenFromGlobalDate(globalDate))
    }

    const onDatePickerChange = (globalDate: GlobalDate): void => {
        publish({
            ...globalDate,
            full: longDateRangeLabelFromStartStopRange(globalDate.start, globalDate.stop),
        })
    }

    const onSelectDateRangeTypeOption = (dateRangeType: DateRangeType): void => {
        publish(globalDateFromDateRangetype(dateRangeType))
    }

    const onClear = (): void => {
        publish(null)
    }

    const onClickOther = (): void => {
        if (globalDate?.dateRangeTypeValue !== customRangeTypeValue) setGlobalDate(null)
        setShowDatePickerRange(true)
    }

    const dropdownLabel = useCallback(
        () =>
            globalDate
                ? globalDate?.dateRangeTypeValue === customRangeTypeValue
                    ? globalDate?.full
                    : DjangIO.app.events.models.DateRangeType.by_value(globalDate?.dateRangeTypeValue)?.label
                : label,
        [globalDate],
    )

    const dropdownOptions = useCallback((): object[] => {
        const dateRangeTypeItems = [
            ...DjangIO.app.events.models.DateRangeType.region_supported_items().filter(
                (dateRangeType) => !dateRangeType.is_future,
            ),
        ]
        return [
            ...dateRangeTypeItems.sort(DjangIO.orderSort).map((dateRangeType) => ({
                ...dateRangeType,
                onClick: () => onSelectDateRangeTypeOption(dateRangeType),
            })),
            ...[
                {
                    onClick: () => onClickOther(),
                    label: "Custom Range",
                    value: customRangeTypeValue,
                },
                ...(globalDate
                    ? [
                          {
                              label: "Clear",
                              onClick: () => onClear(),
                              value: -1,
                          },
                      ]
                    : []),
            ],
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalDate])

    return (
        <Container>
            <SelectDropdown
                isAsync={false}
                components={{
                    IndicatorSeparator: null,
                }}
                isMulti={false}
                isSearchable={false}
                isClearable={true}
                placeholder={label}
                options={dropdownOptions()}
                value={globalDate ? { label: dropdownLabel(), value: globalDate?.dateRangeTypeValue } : null}
                onChange={(dateRangeType) =>
                    dateRangeType && dateRangeType.onClick ? dateRangeType.onClick() : onClear()
                }
            />

            {showDatePickerRange && (
                <DatePickerRange
                    className="date-picker-container-design-system"
                    applyOnClick={(valueFromState, valueToState) => {
                        if (moment(valueFromState).isValid() && moment(valueToState).isValid()) {
                            onDatePickerChange({
                                start: valueFromState,
                                stop: valueToState,
                                dateRangeTypeValue: customRangeTypeValue,
                            })
                            setShowDatePickerRange(false)
                        }
                    }}
                    cancelOnClick={() => {
                        setShowDatePickerRange(false)
                    }}
                    isOpen
                    isRange
                    labelFrom="From"
                    labelTo="To"
                    maxDate={today}
                    onClickOutside={() => setShowDatePickerRange(false)}
                    valueFrom={globalDate?.start || null}
                    valueTo={globalDate?.stop || moment().endOf("day").toDate()}
                />
            )}
        </Container>
    )
}
